import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Parallax } from 'react-scroll-parallax';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faWallet, 
  faCreditCard, 
  faExchangeAlt, 
  faChartLine, 
  faShieldAlt, 
  faCode,
  faGlobe,
  faLock,
  faRocket
} from '@fortawesome/free-solid-svg-icons';
import PaymentFlow from '../components/PaymentFlow';
import DemoSection from '../components/DemoSection';

const Home = () => {

  const redirectToLogin = () => {
    // Use VITE environment variables
    if (import.meta.env.VITE_ENV === "development") {
      const DevUrl = import.meta.env.VITE_DEV_WEB_URL;
      window.location.href = `${DevUrl}/login`;
    } else if (import.meta.env.VITE_ENV === "production") {
      const ProdUrl = import.meta.env.VITE_WEB_URL;
      window.location.href = `${ProdUrl}/login`;
    } else {
      console.error("Unknown environment");
    }
  };
  

  return (
    <div className="min-h-screen bg-white text-gray-800 overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex flex-col justify-center px-4 sm:px-6 lg:px-8 pt-16 pb-12 sm:pt-20 sm:pb-16 lg:pt-24 lg:pb-20 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.03] pointer-events-none"></div>
        
        {/* Background Gradient Blobs */}
        <div className="absolute -top-40 -left-40 w-80 h-80 sm:w-96 sm:h-96 bg-primary/10 rounded-full blur-3xl opacity-30 animate-pulse-slow"></div>
        <div className="absolute -bottom-40 -right-40 w-80 h-80 sm:w-96 sm:h-96 bg-secondary/10 rounded-full blur-3xl opacity-30 animate-pulse-slow delay-1000"></div>
        
        {/* Main Content */}
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-16">
            {/* Left Content */}
            <div className="w-full lg:w-1/2 text-center lg:text-left space-y-6 sm:space-y-8">
              {/* Badge */}
              <div className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-primary/5 border border-primary/10">
                <div className="w-2 h-2 rounded-full bg-primary animate-pulse"></div>
                <span className="ml-2 text-sm sm:text-base font-medium text-primary">Web3-to-Web2 Bridge</span>
              </div>

              {/* Main Heading */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight">
                Seamless <br className="hidden sm:block" /><span className="text-gradient">Crypto to Fiat</span> <br className="hidden sm:block" />Payments
              </h1>

              {/* Description */}
              {/* <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto lg:mx-0">
                Bridge the gap between crypto and traditional payment systems. Accept crypto payments and receive fiat in your bank account with zero volatility risk.
              </p> */}

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 sm:gap-6 mt-8">
                <button
                  onClick={redirectToLogin}
                  className="w-full sm:w-auto px-6 py-3 sm:py-4 bg-primary text-white text-base sm:text-lg font-semibold rounded-xl shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 transition-all duration-200"
                >
                  Get Started
                </button>
                <Link
                  to="/learn-more"
                  className="w-full sm:w-auto px-6 py-3 sm:py-4 bg-white text-gray-800 text-base sm:text-lg font-semibold rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                >
                  Learn More
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 sm:gap-8 mt-12 text-center lg:text-left">
                {[
                  { value: '$10M+', label: 'Total Volume' },
                  { value: '50K+', label: 'Transactions' },
                  { value: '99.9%', label: 'Uptime' }
                ].map((stat, index) => (
                  <div key={index} className="space-y-2">
                    <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">{stat.value}</div>
                    <div className="text-sm sm:text-base text-gray-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Content - Feature Grid */}
            <div className="w-full lg:w-1/2">
              <div className="grid grid-cols-2 gap-4 sm:gap-6">
                {[
                  {
                    icon: faWallet,
                    title: "Multi-Wallet",
                    desc: "Support for all major Web3 wallets"
                  },
                  {
                    icon: faCreditCard,
                    title: "Instant Payout",
                    desc: "Receive fiat within minutes"
                  },
                  {
                    icon: faShieldAlt,
                    title: "Secure Escrow",
                    desc: "Smart contract backed security"
                  },
                  {
                    icon: faChartLine,
                    title: "Real-time Rates",
                    desc: "Dynamic market-based pricing"
                  }
                ].map((feature, index) => (
                  <div
                    key={index}
                    className="group p-6 bg-white rounded-2xl border border-gray-100 hover:border-primary/20 shadow-sm hover:shadow-lg transition-all duration-200"
                  >
                    <div className="w-12 h-12 mb-4 rounded-lg bg-primary/5 group-hover:bg-primary/10 flex items-center justify-center transition-colors duration-200">
                      <FontAwesomeIcon icon={feature.icon} className="text-xl text-primary" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-semibold mb-2 text-gray-900">{feature.title}</h3>
                    <p className="text-sm sm:text-base text-gray-600">{feature.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Section with improved spacing */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <DemoSection />
      </section>

      {/* Payment Flow Section */}
      <section className="py-16 sm:py-20 lg:py-24">
        <PaymentFlow />
      </section>

      {/* CTA Section */}
      <section className="relative py-16 sm:py-20 lg:py-24 bg-gradient-to-r from-primary/5 to-secondary/5 overflow-hidden">
        <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 lg:p-16">
            <div className="text-center space-y-6 sm:space-y-8">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
                Ready to Start Accepting <br className="hidden sm:block" />Crypto Payments?
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto">
                Join thousands of businesses already using UniPay to bridge the Web3 and Web2 payment gap.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 pt-4">
                <Link
                  to="/get-started"
                  className="w-full sm:w-auto px-8 py-4 bg-primary text-white text-base sm:text-lg font-semibold rounded-xl shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 transition-all duration-200"
                >
                  Create Free Account
                </Link>
                <Link
                  to="/contact"
                  className="w-full sm:w-auto px-8 py-4 bg-white text-gray-800 text-base sm:text-lg font-semibold rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                >
                  Contact Sales
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
